import { useState, useEffect, useRef } from 'react'

import { cn } from '@/shared/lib/cn'
import { useConnection } from '@/shared/contexts/ConnectionContext'
import { ui } from '@/shared/strings/ui'

const MIN_VISIBLE_MS = 1000

export function ConnectionIndicator(): React.ReactElement {
  const { status, pendingMutationsCount } = useConnection()

  // Keep syncing bar visible for at least MIN_VISIBLE_MS to avoid flicker
  const [showSyncing, setShowSyncing] = useState(false)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isSyncing = pendingMutationsCount > 0

  useEffect(() => {
    if (isSyncing) {
      // Syncing started — show immediately, cancel any pending hide
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current)
        hideTimerRef.current = null
      }
      setShowSyncing(true)
    } else if (showSyncing) {
      // Syncing ended — hide after minimum visible duration
      hideTimerRef.current = setTimeout(() => {
        setShowSyncing(false)
        hideTimerRef.current = null
      }, MIN_VISIBLE_MS)
    }
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    }
  }, [isSyncing]) // eslint-disable-line react-hooks/exhaustive-deps

  const isOffline = status === 'offline'
  const isReconnecting = status === 'reconnecting'
  const hasBar = isOffline || isReconnecting || showSyncing

  return (
    <div
      className={cn(
        'pointer-events-none fixed inset-x-0 top-0 z-50 transition-transform duration-300 ease-in-out',
        hasBar ? 'translate-y-0' : '-translate-y-full',
      )}
    >
      {isOffline ? (
        <div className="bg-transparent px-4 py-2 text-center text-sm text-status-warning">
          {ui.connection.offline}
        </div>
      ) : isReconnecting ? (
        <div className="bg-transparent px-4 py-2 text-center text-sm text-status-pending">
          {ui.connection.reconnecting}
        </div>
      ) : (
        <div className="bg-transparent px-4 py-2 text-center text-sm text-primary">
          {ui.connection.syncing}
        </div>
      )}
    </div>
  )
}
